<template>
  <!-- User Account Management Modal - Following Bootstrap data-bs pattern -->
  <div class="modal fade user-management-modal"
       :class="{ show: isModalVisible, 'd-block': isModalVisible }"
       :style="isModalVisible ? 'display: block;' : ''"
       data-bs-backdrop="true"
       data-bs-keyboard="true"
       tabindex="-1"
       aria-labelledby="userManagementLabel"
       aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="userManagementLabel">
            <i class="mdi mdi-account-cog me-2"></i>User Account Management
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading user account...</p>
          </div>

          <!-- Employee Info Header -->
          <div v-else class="mb-4">
            <div class="card border-0 bg-light">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="avatar-md me-3">
                    <span class="avatar-title rounded-circle bg-primary text-white font-size-20">
                      {{getInitials((employee && employee.firstName) || (userAccount && userAccount.names), (employee && employee.lastName) || '')}}
                    </span>
                  </div>
                  <div>
                    <h5 class="mb-1">{{ getFullName(employee) || (userAccount && userAccount.names) || (userAccount && userAccount.username) || 'User' }}</h5>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-card-account-details me-1"></i>
                      {{ (employee && employee.empNo) || (userAccount && userAccount.username) || '—' }} | {{ (employee && employee.email) || (userAccount && userAccount.email) || '—' }}
                    </p>
                    <p v-if="(employee && (employee.departmentName || employee.positionTitle)) || (userAccount && userAccount.departmentName)" class="text-muted small mb-0 mt-1">
                      {{ (employee && employee.departmentName) || (userAccount && userAccount.departmentName) || '' }}{{ ((employee && employee.departmentName) || (userAccount && userAccount.departmentName)) && employee && employee.positionTitle ? ' · ' : '' }}{{ (employee && employee.positionTitle) || '' }}
                    </p>
                    <p v-if="userAccount" class="text-muted small mb-0 mt-1">
                      Profile: <strong>{{ userAccount.category || '—' }}</strong>
                      · Status: <strong>{{ userAccount.status || '—' }}</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No User Account: Employee Register only — never when User Management already passed a login row -->
          <div v-if="!loading && !userAccount && !hasSeededLogin" class="text-center py-4">
            <div class="avatar-lg mx-auto mb-4">
              <div class="avatar-title bg-light rounded-circle text-primary">
                <i class="mdi mdi-account-off font-size-36"></i>
              </div>
            </div>
            <h5>No User Account Found</h5>
            <p class="text-muted mb-2">This employee doesn't have a system user account yet.</p>
            <p class="text-muted small mb-4">Create one and choose an <strong>access profile</strong> (e.g. Department HR) so they can log in and see only their department’s data.</p>

            <button @click="createNewAccount" class="btn btn-primary">
              <i class="mdi mdi-account-plus me-1"></i>Create User Account
            </button>
          </div>

          <!-- Seeded login but still refreshing (should be rare) -->
          <div v-if="!loading && !userAccount && hasSeededLogin" class="text-center py-3 text-muted">
            <div class="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading account details…
          </div>

          <!-- Create User Form -->
          <div v-if="showCreateForm && !userAccount" class="mt-4">
            <h6 class="mb-3">Create New User Account</h6>

            <!-- Employee department & access profile help -->
            <div class="alert alert-info py-2 mb-3 small">
              <strong>This employee’s department:</strong> {{ employee.departmentName || 'Not set' }}.
              <span class="d-block mt-1">Choose an <strong>Access profile</strong> to set what they can do and see. For example: <strong>Department HR</strong> = they only see data for their department ({{ employee.departmentName || 'above' }}); <strong>Head of HR</strong> = county-wide.</span>
            </div>

            <!-- Loading access profiles -->
            <div v-if="loadingCategories" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              <span class="ms-2">Loading access profiles...</span>
            </div>

            <form v-else @submit.prevent="createUserAccount">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Username <span class="text-danger">*</span></label>
                  <input v-model="newUser.username" type="text" class="form-control" required>
                  <small class="text-muted">Suggested: {{employee.nationalId || employee.empNo}}</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Password <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input v-model="newUser.password" :type="showPassword ? 'text' : 'password'" class="form-control" required>
                    <button @click="showPassword = !showPassword" class="btn btn-outline-secondary" type="button">
                      <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                    </button>
                    <button @click="generatePassword" class="btn btn-outline-primary" type="button" title="Generate Password">
                      <i class="mdi mdi-refresh"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Access profile <span class="text-danger">*</span></label>
                  <select v-model="newUser.category" class="form-select" required>
                    <option value="" disabled>Select profile — e.g. Department HR (see only their dept), Head of HR (county-wide)...</option>
                    <option v-for="category in availableCategories" :key="category.id" :value="category.categoryName">
                      {{category.categoryName}}
                      <span v-if="category.usersLimit > 0">
                        ({{category.currentUsers || 0}}/{{category.usersLimit}})
                      </span>
                      <span v-else> (Unlimited)</span>
                    </option>
                  </select>
                  <small v-if="userCategories.length === 0" class="text-warning">
                    <i class="mdi mdi-alert me-1"></i>No access profiles available. Create them under System Management → Access profiles & roles.
                  </small>
                  <small v-else-if="selectedCategoryInfo" class="text-muted d-block">
                    <i class="mdi mdi-information me-1"></i>
                    {{selectedCategoryInfo.availableSlots === -1 ? 'Unlimited slots' : selectedCategoryInfo.availableSlots + ' slot(s) available'}}
                  </small>

                  <!-- Display roles for selected category -->
                  <div v-if="selectedCategoryInfo && selectedCategoryInfo.roles && selectedCategoryInfo.roles.length > 0" class="mt-2">
                    <small class="text-muted d-block mb-1">
                      <i class="mdi mdi-shield-account me-1"></i>
                      <strong>Assigned Roles ({{selectedCategoryInfo.roles.length}}):</strong>
                    </small>
                    <div class="d-flex flex-wrap gap-1">
                      <span v-for="(role, index) in selectedCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                        {{role}}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="selectedCategoryInfo && selectedCategoryInfo.roles && selectedCategoryInfo.roles.length === 0" class="mt-2">
                    <small class="text-muted">
                      <i class="mdi mdi-alert-circle me-1"></i>No roles assigned to this profile. Assign in Access profiles & roles.
                    </small>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Require OTP</label>
                  <select v-model="newUser.requireOTP" class="form-select">
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button @click="cancelCreateForm" type="button" class="btn btn-light">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="creating || availableCategories.length === 0">
                  <span v-if="creating" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="mdi mdi-check me-1"></i>
                  Create Account
                </button>
              </div>
            </form>
          </div>

          <!-- Existing User Account -->
          <div v-if="!loading && userAccount && !showCreateForm">
            <!-- User Status Overview -->
            <div class="row mb-4">
              <div class="col-md-4 mb-3">
                <div class="card border-0" :class="getUserStatusCardClass(userAccount.status)">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Account Status</h6>
                    <h4 :class="getUserStatusTextClass(userAccount.status)">
                      {{formatStatus(userAccount.status)}}
                    </h4>
                  </div>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="card border-0 bg-light">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Username</h6>
                    <h5 class="mb-0">{{userAccount.username}}</h5>
                  </div>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="card border-0 bg-light">
                  <div class="card-body text-center">
                    <h6 class="text-muted mb-2">Access profile</h6>
                    <span class="badge bg-primary font-size-14">{{userAccount.category}}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- User Details -->
            <div class="card border mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0">Account Details</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Email</label>
                    <p class="mb-0">{{userAccount.email || 'N/A'}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Phone Number</label>
                    <p class="mb-0">{{userAccount.phoneNumber || 'N/A'}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Last Login</label>
                    <p class="mb-0">{{formatDateTime(userAccount.lastLogin)}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Created At</label>
                    <p class="mb-0">{{formatDateTime(userAccount.createdAt)}}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Require OTP</label>
                    <p class="mb-0">
                      <span :class="userAccount.requireOTP === 'true' ? 'badge bg-success' : 'badge bg-secondary'">
                        {{userAccount.requireOTP === 'true' ? 'Yes' : 'No'}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Password Changed</label>
                    <p class="mb-0">
                      <span :class="userAccount.passwordChanged === 'true' ? 'badge bg-success' : 'badge bg-warning'">
                        {{userAccount.passwordChanged === 'true' ? 'Yes' : 'No'}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted small">Failed Login Attempts</label>
                    <p class="mb-0">
                      <span :class="getFailedAttemptsClass(userAccount.failedLoginAttempts)">
                        {{userAccount.failedLoginAttempts || 0}}
                      </span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3" v-if="userAccount.accountLockedUntil">
                    <label class="text-muted small">Account Locked Until</label>
                    <p class="mb-0 text-danger">{{formatDateTime(userAccount.accountLockedUntil)}}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Access profile (editable) -->
            <div class="card border mb-4">
              <div class="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 class="mb-0">
                  <i class="mdi mdi-shield-account me-2"></i>Access profile permissions
                </h6>
                <span class="badge bg-primary">{{ profileEdit.category || userAccount.category || '—' }}</span>
              </div>
              <div class="card-body">
                <p class="text-muted small mb-3">
                  Change this user’s <strong>access profile</strong> to update which roles (permissions) they receive. Roles are defined under System Management → Access profiles &amp; roles.
                </p>
                <div class="row g-3 align-items-end">
                  <div class="col-md-8">
                    <label class="form-label">Access profile <span class="text-danger">*</span></label>
                    <select v-model="profileEdit.category" class="form-select" :disabled="actionLoading || loadingCategories">
                      <option value="" disabled>Select access profile…</option>
                      <option v-for="category in userCategories" :key="category.id" :value="category.categoryName">
                        {{ category.categoryName }}
                        <template v-if="category.usersLimit > 0">
                          ({{ category.currentUsers || 0 }}/{{ category.usersLimit }})
                        </template>
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <button
                      type="button"
                      class="btn btn-primary w-100"
                      :disabled="actionLoading || !profileEdit.category || profileEdit.category === userAccount.category"
                      @click="saveAccessProfile"
                    >
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-content-save me-1"></i>
                      Save profile
                    </button>
                  </div>
                </div>

                <div v-if="profileCategoryInfo && profileCategoryInfo.roles && profileCategoryInfo.roles.length" class="mt-3">
                  <p class="text-muted small mb-2">
                    This profile grants access to <strong>{{ profileCategoryInfo.roles.length }}</strong> role(s):
                  </p>
                  <div class="d-flex flex-wrap gap-2">
                    <span v-for="(role, index) in profileCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                      <i class="mdi mdi-check-circle me-1"></i>{{ role }}
                    </span>
                  </div>
                </div>
                <div v-else-if="profileEdit.category" class="mt-3 text-muted small">
                  <i class="mdi mdi-alert-circle me-1"></i>No roles assigned to this profile yet. Assign roles under Access profiles &amp; roles.
                </div>
              </div>
            </div>

            <!-- Account Actions -->
            <div class="card border">
              <div class="card-header bg-light">
                <h6 class="mb-0">Account Actions</h6>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <!-- Reset Password -->
                  <div class="col-md-6">
                    <button @click="showResetPasswordForm = true" class="btn btn-warning w-100" :disabled="actionLoading">
                      <i class="mdi mdi-lock-reset me-1"></i>Reset Password
                    </button>
                  </div>

                  <!-- Unlock Account (if locked) -->
                  <div class="col-md-6" v-if="isAccountLocked() || isStatus(userAccount.status, 'blocked')">
                    <button @click="unlockAccount" class="btn btn-info w-100" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-lock-open me-1"></i>
                      Unblock Account
                    </button>
                  </div>

                  <!-- Activate/Suspend -->
                  <div class="col-md-6" v-if="isStatus(userAccount.status, 'suspended')">
                    <button @click="activateAccount" class="btn btn-success w-100" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-account-check me-1"></i>
                      Activate Account
                    </button>
                  </div>

                  <div class="col-md-6" v-if="isStatus(userAccount.status, 'active')">
                    <button @click="showSuspendForm = true" class="btn btn-warning w-100" :disabled="actionLoading">
                      <i class="mdi mdi-account-off me-1"></i>Suspend Account
                    </button>
                  </div>

                  <!-- Update Settings -->
                  <div class="col-md-6">
                    <button @click="showUpdateFormMethod" class="btn btn-primary w-100" :disabled="actionLoading">
                      <i class="mdi mdi-account-edit me-1"></i>Update Settings
                    </button>
                  </div>

                  <!-- Delete Account -->
                  <div class="col-md-6">
                    <button @click="showDeleteConfirm = true" class="btn btn-danger w-100" :disabled="actionLoading">
                      <i class="mdi mdi-delete me-1"></i>Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reset Password Form -->
            <div v-if="showResetPasswordForm" class="card border border-warning mt-4">
              <div class="card-header bg-warning bg-opacity-10">
                <h6 class="mb-0 text-warning">
                  <i class="mdi mdi-lock-reset me-1"></i>Reset Password
                </h6>
              </div>
              <div class="card-body">
                <form @submit.prevent="resetPassword">
                  <div class="mb-3">
                    <label class="form-label">New Password <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <input v-model="resetPasswordData.newPassword" :type="showPassword ? 'text' : 'password'" class="form-control" required>
                      <button @click="showPassword = !showPassword" class="btn btn-outline-secondary" type="button">
                        <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                      </button>
                      <button @click="generatePasswordForReset" class="btn btn-outline-primary" type="button">
                        <i class="mdi mdi-refresh"></i>
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input v-model="resetPasswordData.forceChange" class="form-check-input" type="checkbox" id="forceChangeCheck">
                      <label class="form-check-label" for="forceChangeCheck">
                        Force password change on next login
                      </label>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2">
                    <button @click="showResetPasswordForm = false; resetPasswordData = {newPassword: '', forceChange: true}" type="button" class="btn btn-light">Cancel</button>
                    <button type="submit" class="btn btn-warning" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-check me-1"></i>
                      Reset Password
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Update Form -->
            <div v-if="showUpdateForm" class="card border border-primary mt-4">
              <div class="card-header bg-primary bg-opacity-10">
                <h6 class="mb-0 text-primary">
                  <i class="mdi mdi-account-edit me-1"></i>Update Account Settings
                </h6>
              </div>
              <div class="card-body">
                <!-- Loading Categories -->
                <div v-if="loadingCategories" class="text-center py-3 mb-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <span class="ms-2">Loading categories...</span>
                </div>

                <form v-else @submit.prevent="updateAccount">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Status</label>
                      <select v-model="updateData.status" class="form-select">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Access profile</label>
                      <select v-model="updateData.category" class="form-select">
                        <option v-for="category in userCategories" :key="category.id" :value="category.categoryName">
                          {{category.categoryName}}
                          <span v-if="category.usersLimit > 0">
                            ({{category.currentUsers || 0}}/{{category.usersLimit}})
                          </span>
                          <span v-else> (Unlimited)</span>
                        </option>
                      </select>
                      <small v-if="updateSelectedCategoryInfo" class="text-muted d-block">
                        <i class="mdi mdi-information me-1"></i>
                        {{updateSelectedCategoryInfo.availableSlots === -1 ? 'Unlimited slots' : updateSelectedCategoryInfo.availableSlots + ' slot(s) available'}}
                      </small>

                      <!-- Display roles for selected category -->
                      <div v-if="updateSelectedCategoryInfo && updateSelectedCategoryInfo.roles && updateSelectedCategoryInfo.roles.length > 0" class="mt-2">
                        <small class="text-muted d-block mb-1">
                          <i class="mdi mdi-shield-account me-1"></i>
                          <strong>Assigned Roles ({{updateSelectedCategoryInfo.roles.length}}):</strong>
                        </small>
                        <div class="d-flex flex-wrap gap-1">
                          <span v-for="(role, index) in updateSelectedCategoryInfo.roles" :key="index" class="badge bg-info text-dark bg-opacity-25">
                            {{role}}
                          </span>
                        </div>
                      </div>
                      <div v-else-if="updateSelectedCategoryInfo && updateSelectedCategoryInfo.roles && updateSelectedCategoryInfo.roles.length === 0" class="mt-2">
                        <small class="text-muted">
                          <i class="mdi mdi-alert-circle me-1"></i>No roles assigned to this profile.
                        </small>
                      </div>
                    </div>

                    <div class="col-md-6 mb-3">
                      <label class="form-label">Require OTP</label>
                      <select v-model="updateData.requireOTP" class="form-select">
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2">
                    <button @click="showUpdateForm = false" type="button" class="btn btn-light">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="actionLoading">
                      <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="mdi mdi-check me-1"></i>
                      Update Settings
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Suspend Confirmation -->
            <div v-if="showSuspendForm" class="card border border-warning mt-4">
              <div class="card-header bg-warning bg-opacity-10">
                <h6 class="mb-0 text-warning">
                  <i class="mdi mdi-alert me-1"></i>Suspend Account
                </h6>
              </div>
              <div class="card-body">
                <p class="text-muted">Please provide a reason for suspending this account:</p>
                <textarea v-model="suspendReason" class="form-control mb-3" rows="3" placeholder="Reason for suspension..."></textarea>

                <div class="d-flex justify-content-end gap-2">
                  <button @click="showSuspendForm = false; suspendReason = ''" type="button" class="btn btn-light">Cancel</button>
                  <button @click="suspendAccount" class="btn btn-warning" :disabled="actionLoading">
                    <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="mdi mdi-check me-1"></i>
                    Confirm Suspend
                  </button>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation -->
            <div v-if="showDeleteConfirm" class="card border border-danger mt-4">
              <div class="card-header bg-danger bg-opacity-10">
                <h6 class="mb-0 text-danger">
                  <i class="mdi mdi-alert me-1"></i>Delete User Account
                </h6>
              </div>
              <div class="card-body">
                <div class="alert alert-danger">
                  <strong>Warning!</strong> This action will deactivate the user account. This is a soft delete and can be reversed by reactivating the account.
                </div>

                <p class="text-muted">Please provide a reason for deleting this account:</p>
                <textarea v-model="deleteReason" class="form-control mb-3" rows="3" placeholder="Reason for deletion..."></textarea>

                <div class="d-flex justify-content-end gap-2">
                  <button @click="showDeleteConfirm = false; deleteReason = ''" type="button" class="btn btn-light">Cancel</button>
                  <button @click="deleteAccount" class="btn btn-danger" :disabled="actionLoading">
                    <span v-if="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="mdi mdi-check me-1"></i>
                    Confirm Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Lighter backdrop for user management modal */
.user-management-modal ~ .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.35) !important;
}

.user-management-modal ~ .modal-backdrop.show {
  opacity: 0.6 !important;
}

/* Better modal shadow */
.user-management-modal .modal-content {
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 0.5rem;
}
</style>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import moment from "moment";

export default {
  name: "UserManagementModal",
  props: {
    employee: {
      type: Object,
      default: null
    },
    /** When opening from User Management, pass the listed user so we don't depend on employeeID link. */
    existingUser: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      useBootstrapModal: false,
      bsModal: null,
      isModalVisible: false,
      customBackdropEl: null,
      loading: false,
      creating: false,
      actionLoading: false,
      userAccount: null,
      showCreateForm: false,
      showResetPasswordForm: false,
      showUpdateForm: false,
      showSuspendForm: false,
      showDeleteConfirm: false,
      showPassword: false,
      userCategories: [],
      loadingCategories: false,
      newUser: {
        username: '',
        password: '',
        category: '',
        requireOTP: 'false'
      },
      resetPasswordData: {
        newPassword: '',
        forceChange: true
      },
      updateData: {
        status: '',
        category: '',
        requireOTP: ''
      },
      profileEdit: {
        category: ''
      },
      suspendReason: '',
      deleteReason: ''
    }
  },
  computed: {
    /** True if we already know this modal is managing an existing login (User Management list or linked userID). */
    hasSeededLogin() {
      const seed = this.existingUser || null;
      const emp = this.employee || null;
      const id = seed?.userID ?? seed?.userId ?? emp?.userID ?? emp?.userId;
      return id != null && String(id).trim() !== '' && String(id) !== '0';
    },
    availableCategories() {
      // Filter categories that are available (not full)
      return this.userCategories.filter(cat =>
          cat.availability === 'unlimited' || cat.availability === 'available'
      );
    },
    selectedCategoryInfo() {
      if (!this.newUser.category) return null;
      return this.userCategories.find(cat => cat.categoryName === this.newUser.category);
    },
    updateSelectedCategoryInfo() {
      if (!this.updateData.category) return null;
      return this.userCategories.find(cat => cat.categoryName === this.updateData.category);
    },
    currentUserCategoryInfo() {
      if (!this.userAccount || !this.userAccount.category || this.userCategories.length === 0) return null;
      return this.userCategories.find(cat => cat.categoryName === this.userAccount.category);
    },
    profileCategoryInfo() {
      const name = this.profileEdit?.category || this.userAccount?.category;
      if (!name || !this.userCategories.length) return null;
      return this.userCategories.find(cat => cat.categoryName === name) || null;
    }
  },
  watch: {
    employee: {
      handler(newVal) {
        if (newVal || this.existingUser) {
          this.loadUserAccount();
        }
      },
      deep: true,
      immediate: true
    },
    existingUser: {
      handler() {
        if (this.employee || this.existingUser) this.loadUserAccount();
      },
      deep: true
    }
  },
  created() {
    // Seed immediately so we never flash "No User Account" for listed users
    if (this.existingUser && (this.existingUser.userID || this.existingUser.userId)) {
      this.applyUserAccount({ ...this.existingUser });
    }
  },
  mounted() {
    this.getUserCategories();
    this.$nextTick(() => {
      const el = this.$el;
      this.isModalVisible = true;
      if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        this.useBootstrapModal = true;
        this.bsModal = new bootstrap.Modal(el);
        el.addEventListener('hidden.bs.modal', () => {
          this.$emit('close');
        });
        this.bsModal.show();
      } else {
        this.useBootstrapModal = false;
        document.body.classList.add('modal-open');
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
        this.customBackdropEl = backdrop;
      }
    });
  },
  beforeUnmount() {
    this.cleanupNonBootstrapModal();
    this.bsModal = null;
  },
  methods: {
    closeModalFromEvent(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (this.useBootstrapModal && this.bsModal) {
        this.bsModal.hide();
        return;
      }
      this.cleanupNonBootstrapModal();
      this.$emit('close');
    },
    cleanupNonBootstrapModal() {
      if (!this.useBootstrapModal) {
        document.body.classList.remove('modal-open');
        if (this.customBackdropEl && this.customBackdropEl.parentNode) {
          this.customBackdropEl.parentNode.removeChild(this.customBackdropEl);
        }
      }
      this.customBackdropEl = null;
      this.isModalVisible = false;
    },
    resolveSeedUserId() {
      const seed = this.existingUser || {};
      const emp = this.employee || {};
      const id = seed.userID ?? seed.userId ?? emp.userID ?? emp.userId;
      if (id == null || String(id).trim() === '' || String(id) === '0') return null;
      return String(id);
    },
    loadUserAccount() {
      if (!this.employee && !this.existingUser) {
        return;
      }

      this.showCreateForm = false;
      this.showResetPasswordForm = false;
      this.showUpdateForm = false;
      this.showSuspendForm = false;
      this.showDeleteConfirm = false;

      const seedId = this.resolveSeedUserId();
      // User Management already supplied the full account row; avoid duplicate lookups.
      if (seedId && this.existingUser) {
        this.applyUserAccount({ ...this.existingUser, userID: this.existingUser.userID ?? this.existingUser.userId });
        this.loading = false;
        return;
      } else if (this.userAccount && this.userAccount.userID) {
        this.loading = false;
      } else {
        this.loading = true;
      }

      const data = new FormData();
      const userId = seedId;
      const employeeId = this.employee?.id;

      if (userId) {
        data.append("function", "getUserByUserId");
        data.append("userId", String(userId));
      } else if (employeeId != null && String(employeeId).trim() !== "" && String(employeeId) !== "0") {
        data.append("function", "getUserByEmployeeId");
        data.append("employeeId", String(employeeId));
        if (this.employee?.email) data.append("email", String(this.employee.email));
        if (this.employee?.nationalId) data.append("idNo", String(this.employee.nationalId));
        if (this.employee?.empNo) data.append("username", String(this.employee.empNo));
      } else if (this.existingUser) {
        data.append("function", "getUserByEmployeeId");
        if (this.existingUser.username) data.append("username", String(this.existingUser.username));
        if (this.existingUser.email) data.append("email", String(this.existingUser.email));
        if (this.existingUser.idNo) data.append("idNo", String(this.existingUser.idNo));
      } else {
        this.loading = false;
        this.userAccount = null;
        this.newUser.username = this.employee?.nationalId || this.employee?.empNo || '';
        return;
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            const body = res?.data || {};
            const payload = body.data != null ? body.data : body;
            let user = payload?.user ?? null;
            if (!user && body.success !== false && payload && (payload.userID || payload.username)) {
              user = payload;
            }
            // Some gateways nest under data.data
            if (!user && payload?.data?.user) user = payload.data.user;

            if (user && (user.userID || user.userId || user.username)) {
              if (!user.userID && user.userId) user = { ...user, userID: user.userId };
              this.applyUserAccount(user);
            } else if (seedId && this.existingUser) {
              // Keep the seeded list user if API is soft-fail
              this.applyUserAccount({
                ...this.existingUser,
                userID: this.existingUser.userID ?? this.existingUser.userId
              });
            } else if (!seedId) {
              this.userAccount = null;
              this.newUser.username = this.employee?.nationalId || this.employee?.empNo || '';
            }

          })
          .catch((e) => {
            this.loading = false;
            if (seedId && this.existingUser) {
              this.applyUserAccount({
                ...this.existingUser,
                userID: this.existingUser.userID ?? this.existingUser.userId
              });
              return;
            }
            this.userAccount = null;
            if (this.$toast) {
              this.$toast.error("Failed to load user account: " + (e.message || 'Unknown error'));
            }
          });
    },
    applyUserAccount(user) {
      if (!user) {
        this.userAccount = null;
        return;
      }
      const normalized = { ...user };
      if (!normalized.userID && normalized.userId) {
        normalized.userID = normalized.userId;
      }
      this.userAccount = normalized;
      this.updateData = {
        status: normalized.status || '',
        category: normalized.category || '',
        requireOTP: normalized.requireOTP != null ? String(normalized.requireOTP) : 'false'
      };
      this.profileEdit = {
        category: normalized.category || ''
      };
    },

    getUserCategories() {
      this.loadingCategories = true;

      const data = new FormData();
      data.append("function", "getCategories");

      execute(data, authUrl)
          .then((res) => {
            this.loadingCategories = false;
            if (res.data.success) {
              // Process categories to include availability information
              this.userCategories = (res.data.data.categories || []).map(cat => {
                const availableSlots = cat.usersLimit === 0
                    ? -1
                    : Math.max(0, cat.usersLimit - cat.currentUsers);

                return {
                  ...cat,
                  availableSlots: availableSlots,
                  availability: cat.usersLimit === 0
                      ? 'unlimited'
                      : (availableSlots > 0 ? 'available' : 'full')
                };
              });

              // Set default category if available and not already set
              if (this.userCategories.length > 0 && !this.newUser.category) {
                // Try to find "Employee" category as default
                const employeeCategory = this.userCategories.find(cat =>
                    cat.categoryName.toLowerCase() === 'employee' &&
                    (cat.availability === 'unlimited' || cat.availability === 'available')
                );
                if (employeeCategory) {
                  this.newUser.category = employeeCategory.categoryName;
                } else {
                  // Use first available category
                  const availableCategory = this.userCategories.find(cat =>
                      cat.availability === 'unlimited' || cat.availability === 'available'
                  );
                  if (availableCategory) {
                    this.newUser.category = availableCategory.categoryName;
                  }
                }
              }
            } else {
              if (this.$toast) {
                this.$toast.error("Failed to load categories: " + res.data.message);
              }
              this.userCategories = [];
            }
          })
          .catch((e) => {
            this.loadingCategories = false;
            if (this.$toast) {
              this.$toast.error("Failed to load categories: " + (e.message || 'Unknown error'));
            }
            this.userCategories = [];
          });
    },

    createNewAccount() {
      this.showCreateForm = true;
      this.getUserCategories();
    },

    showUpdateFormMethod() {
      this.showUpdateForm = true;
      if (this.userCategories.length === 0) {
        this.getUserCategories();
      }
    },

    cancelCreateForm() {
      this.showCreateForm = false;
      this.newUser = {
        username: this.employee.nationalId || this.employee.empNo || '',
        password: '',
        category: '',
        requireOTP: 'false'
      };
    },

    createUserAccount() {
      this.creating = true;

      // Get the selected category object to extract both name and ID
      const selectedCategory = this.userCategories.find(cat => cat.categoryName === this.newUser.category);

      if (!selectedCategory) {
        if (this.$toast) {
          this.$toast.error("Invalid category selected");
        }
        this.creating = false;
        return;
      }

      const data = new FormData();
      data.append("function", "createUserAccount");
      data.append("employeeId", this.employee.id);
      data.append("username", this.newUser.username);
      data.append("password", this.newUser.password);
      data.append("category", selectedCategory.categoryName);
      data.append("categoryId", selectedCategory.id);
      data.append("requireOTP", this.newUser.requireOTP);

      execute(data, authUrl)
          .then((res) => {
            this.creating = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showCreateForm = false;
              this.loadUserAccount();
              this.$emit('user-created');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.creating = false;
            if (this.$toast) {
              this.$toast.error("Failed to create user account: " + (e.message || 'Unknown error'));
            }
          });
    },

    resetPassword() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "resetUserPassword");
      data.append("userId", this.userAccount.userID);
      data.append("newPassword", this.resetPasswordData.newPassword);
      data.append("forceChange", this.resetPasswordData.forceChange ? 'true' : 'false');

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showResetPasswordForm = false;
              this.resetPasswordData = { newPassword: '', forceChange: true };
              this.loadUserAccount();
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to reset password: " + (e.message || 'Unknown error'));
            }
          });
    },

    updateAccount() {
      this.actionLoading = true;

      // Get the selected category object to extract both name and ID
      const selectedCategory = this.userCategories.find(cat => cat.categoryName === this.updateData.category);

      if (!selectedCategory) {
        if (this.$toast) {
          this.$toast.error("Invalid category selected");
        }
        this.actionLoading = false;
        return;
      }

      const data = new FormData();
      data.append("function", "updateUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("status", this.updateData.status);
      data.append("category", selectedCategory.categoryName);
      data.append("categoryId", selectedCategory.id);
      data.append("requireOTP", this.updateData.requireOTP);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showUpdateForm = false;
              this.loadUserAccount();
              this.$emit('user-updated');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to update account: " + (e.message || 'Unknown error'));
            }
          });
    },

    saveAccessProfile() {
      if (!this.userAccount?.userID || !this.profileEdit.category) return;
      const selectedCategory = this.userCategories.find(cat => cat.categoryName === this.profileEdit.category);
      if (!selectedCategory) {
        if (this.$toast) this.$toast.error("Invalid access profile selected");
        else alert("Invalid access profile selected");
        return;
      }
      if (selectedCategory.categoryName === this.userAccount.category) return;

      this.actionLoading = true;
      const data = new FormData();
      data.append("function", "updateUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("status", this.userAccount.status || "active");
      data.append("category", selectedCategory.categoryName);
      data.append("categoryId", selectedCategory.id);
      data.append(
        "requireOTP",
        this.userAccount.requireOTP != null ? String(this.userAccount.requireOTP) : "false"
      );

      execute(data, authUrl)
        .then((res) => {
          this.actionLoading = false;
          if (res.data.success) {
            if (this.$toast) this.$toast.success(res.data.message || "Access profile updated.");
            else alert("Access profile updated.");
            this.loadUserAccount();
            this.$emit("user-updated");
          } else if (this.$toast) {
            this.$toast.error(res.data.message || "Failed to update access profile");
          } else {
            alert(res.data.message || "Failed to update access profile");
          }
        })
        .catch((e) => {
          this.actionLoading = false;
          if (this.$toast) this.$toast.error("Failed to update access profile: " + (e.message || "Unknown error"));
          else alert("Failed to update access profile: " + (e.message || "Unknown error"));
        });
    },

    unlockAccount() {
      if (!confirm('Are you sure you want to unlock this account?')) return;

      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "unlockUserAccount");
      data.append("userId", this.userAccount.userID);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.loadUserAccount();
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to unlock account: " + (e.message || 'Unknown error'));
            }
          });
    },

    suspendAccount() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "suspendUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("reason", this.suspendReason);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showSuspendForm = false;
              this.suspendReason = '';
              this.loadUserAccount();
              this.$emit('user-suspended');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to suspend account: " + (e.message || 'Unknown error'));
            }
          });
    },

    activateAccount() {
      if (!confirm('Are you sure you want to activate this account?')) return;

      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "activateUserAccount");
      data.append("userId", this.userAccount.userID);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.loadUserAccount();
              this.$emit('user-activated');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to activate account: " + (e.message || 'Unknown error'));
            }
          });
    },

    deleteAccount() {
      this.actionLoading = true;

      const data = new FormData();
      data.append("function", "deleteUserAccount");
      data.append("userId", this.userAccount.userID);
      data.append("reason", this.deleteReason);

      execute(data, authUrl)
          .then((res) => {
            this.actionLoading = false;
            if (res.data.success) {
              if (this.$toast) {
                this.$toast.success(res.data.message);
              }
              this.showDeleteConfirm = false;
              this.deleteReason = '';
              this.loadUserAccount();
              this.$emit('user-deleted');
            } else {
              if (this.$toast) {
                this.$toast.error(res.data.message);
              }
            }
          })
          .catch((e) => {
            this.actionLoading = false;
            if (this.$toast) {
              this.$toast.error("Failed to delete account: " + (e.message || 'Unknown error'));
            }
          });
    },

    generatePassword() {
      const length = 12;
      const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
      this.newUser.password = password;
    },

    generatePasswordForReset() {
      const length = 12;
      const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
      this.resetPasswordData.newPassword = password;
    },

    isAccountLocked() {
      if (!this.userAccount || !this.userAccount.accountLockedUntil) return false;
      return moment(this.userAccount.accountLockedUntil).isAfter(moment());
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : '') + (lastName ? lastName.charAt(0) : '')).toUpperCase();
    },

    getFullName(emp) {
      if (!emp) return '';
      const parts = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean);
      return parts.join(' ');
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    isStatus(status, expected) {
      return String(status || '').toLowerCase() === String(expected || '').toLowerCase();
    },

    getUserStatusCardClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'bg-success bg-opacity-10';
      if (statusLower === 'suspended') return 'bg-warning bg-opacity-10';
      if (statusLower === 'inactive') return 'bg-danger bg-opacity-10';
      return 'bg-secondary bg-opacity-10';
    },

    getUserStatusTextClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'active') return 'text-success';
      if (statusLower === 'suspended') return 'text-warning';
      if (statusLower === 'inactive') return 'text-danger';
      return 'text-secondary';
    },

    getFailedAttemptsClass(attempts) {
      if (!attempts || attempts === 0) return 'badge bg-success';
      if (attempts < 3) return 'badge bg-warning';
      return 'badge bg-danger';
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      return moment(datetime).format('DD MMM YYYY, HH:mm');
    }
  }
}
</script>

<style scoped>
.avatar-md {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 64px;
  height: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.card {
  transition: all 0.3s ease;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.modal-dialog-scrollable .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>